import {Card, DataStatus, FlagProps, GridPhoto, Parameter, PhotoGrid} from 'platform/components';
import {Box, HStack} from 'platform/foundation';
import {Lightbox, useLightbox, useLightboxActionsFactory} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useGetAuditDataQuery} from '@dms/api/core';
import {PartialVehiclePhotosRoutes} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  downloadFile,
  getPrecedingPlayableVideos,
  isVideoPlayable,
  useBatchDownload,
  Video,
  VideoPreview,
} from '@dms/shared';

import {buildArray, Nullish, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {getGridPhotosFromAudit} from '../utils/getGridPhotosFromAudit';
import {getGridVideosFromAudit} from '../utils/getGridVideosFromAudit';
import {getStatusColorScheme} from '../utils/getStatusColorScheme';
import {getStatusTranslation} from '../utils/getStatusTranslation';

interface RentalOrderPhotoCardProps extends TestIdProps {
  inspectionId: string | Nullish;
  title: string;
}

export const RentalInspectionPhotos = (props: RentalOrderPhotoCardProps) => {
  const {
    data: auditData,
    isLoading: isAuditDataLoading,
    isError: isAuditDataError,
  } = useGetAuditDataQuery({auditId: props.inspectionId ?? ''}, {skip: isNil(props.inspectionId)});

  const photos = getGridPhotosFromAudit(auditData?.[0]?.categories);
  const videos = getGridVideosFromAudit(auditData?.[0]?.categories);
  const updatedAt = auditData?.[0]?.updateAt;
  const state = auditData?.[0]?.state;

  const formatDateTime = useDateTimeFormatter();
  const actionsFactory = useLightboxActionsFactory();
  const [lightboxControls, {onOpen: onLightboxOpen}] = useLightbox(
    `rentalOrderPhotos-${props.inspectionId}`
  );
  const {
    handleDownload,
    isLoading: isBatchDownloadLoading,
    isStatePending: isBatchDownloadStatePending,
  } = useBatchDownload();

  const handleInspectionGalleryBatchDownload = () =>
    handleDownload({
      vehiclePhotoLocation: PartialVehiclePhotosRoutes.inspections,
      vehicleId: auditData?.[0]?.vehicleId ?? '',
      auditId: props.inspectionId ?? '',
    });

  const makeLightboxPhotos = (photos: GridPhoto[] | Nullish) =>
    actionsFactory.addFullActions(
      photos?.map((image) => ({
        id: image.id,
        url: image.originalUrl?.replace('resize', 'get') ?? image.url.replace('resize', 'get'),
        resizeUrl: image.resizeUrl,
        title: image.title,
        fileName: image.title || image.id ? `${image.title || image.id}.jpg` : undefined,
      }))
    );

  const makeLightboxVideos = (videos: Video[] | Nullish) =>
    actionsFactory.addShareOnlyActions(videos?.filter(isVideoPlayable), (video) => [
      {
        id: 'downloadBtn',
        icon: 'file/download',
        onClick: () => {
          if (isNotNil(video.originUrl)) {
            downloadFile(video.originUrl);
          }
        },
      },
    ]);

  return (
    <DataStatus isLoading={isAuditDataLoading} isError={isAuditDataError} minHeight={24}>
      <Card
        title={props.title}
        parameters={buildArray<Parameter>().when(
          isNotNil(updatedAt),
          `${i18n.t('general.labels.lastUpdated')}: ${formatDateTime('dateTimeShort', parseDate(updatedAt ?? new Date()))}`
        )}
        flags={buildArray<FlagProps>().when(isNotNil(state), {
          size: 'small',
          colorScheme: getStatusColorScheme(state ?? ''),
          label: getStatusTranslation(state ?? ''),
        })}
        actions={[
          {
            type: 'button',
            variant: 'link',
            title: i18n.t('general.actions.downloadAll'),
            leftIcon: 'file/download',
            onClick: handleInspectionGalleryBatchDownload,
            isLoading: isBatchDownloadLoading || isBatchDownloadStatePending,
            isDisabled: !photos.length,
          },
        ]}
      >
        <PhotoGrid
          images={photos}
          onSlideClick={(_, index) => onLightboxOpen(index)}
          data-testid={suffixTestId('rentalOrderPhotos', props)}
        />
        <HStack wrap spacing={4}>
          {videos?.map((video, index) => (
            <Box key={video.id} width={58} height={43}>
              <VideoPreview
                videoAsset={video}
                isLoading={!isVideoPlayable(video)}
                onOpenPreview={() =>
                  onLightboxOpen(getPrecedingPlayableVideos(videos, index) + photos.length)
                }
                data-testid={suffixTestId('rentalOrderVideo', props)}
              />
            </Box>
          ))}
        </HStack>
        <Lightbox
          data-testid={suffixTestId('rentalOrderPhotos', props)}
          controls={lightboxControls}
          images={makeLightboxPhotos(photos)}
          videos={makeLightboxVideos(videos)}
        />
      </Card>
    </DataStatus>
  );
};
